/**
 * Feedback Management Utilities
 * Helper functions for submitting, updating, and tracking institution feedback
 */

import { getSupabaseClient } from '@/lib/supabase/loader';
import { captureException } from '@/lib/sentry';

export interface FeedbackInput {
  title: string;
  description: string;
  feedback_type: 'usability' | 'compliance' | 'integration' | 'feature_request' | 'bug_report' | 'performance';
  category: 'feature' | 'ui_ux' | 'performance' | 'security' | 'regulatory' | 'integration' | 'documentation';
  organization_id: string;
  source: 'in_app' | 'email' | 'interview' | 'survey' | 'support_ticket' | 'manual_entry';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  priority?: 'must_have' | 'should_have' | 'nice_to_have' | 'backlog' | 'blocking';
  context?: Record<string, unknown>;
  assigned_team?: string;
  internal_notes?: string;
}

export interface FeedbackUpdate {
  status?: 'new' | 'under_review' | 'acknowledged' | 'triaged' | 'in_progress' | 'resolved' | 'wont_fix' | 'duplicate';
  assigned_to?: string;
  assigned_team?: string;
  priority?: string;
  severity?: string;
  response_message?: string;
  internal_notes?: string;
  impact_score?: number;
  effort_estimate?: 'XS' | 'S' | 'M' | 'L' | 'XL';
}

/**
 * Team routing based on feedback category
 */
const TEAM_ROUTING: Record<string, string> = {
  compliance: 'Compliance Team',
  security: 'Security Team',
  regulatory: 'Compliance Team',
  feature: 'Product Team',
  ui_ux: 'Product Team',
  performance: 'Engineering Team',
  integration: 'Platform Team',
  documentation: 'Documentation Team',
};

/**
 * Submit new feedback to central repository
 * Automatically routes to appropriate team based on category
 * 
 * @param feedback - Feedback details
 * @returns Created feedback record
 */
export async function submitFeedback(feedback: FeedbackInput) {
  try {
    const supabase = await getSupabaseClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    // Determine team assignment based on category
    const assignedTeam = feedback.assigned_team || TEAM_ROUTING[feedback.category] || 'Product Team';

    // Insert feedback
    const { data, error } = await supabase
      .from('institution_feedback')
      .insert([
        {
          ...feedback,
          submitted_by: user.id,
          severity: feedback.severity || 'medium',
          priority: feedback.priority || 'backlog',
          assigned_team: assignedTeam,
          context: feedback.context || {},
          internal_notes: feedback.internal_notes || '',
        },
      ])
      .select()
      .single();

    if (error) throw error;

    // Log to audit trail
    await supabase.from('audit_logs').insert({
      actor_id: user.id,
      actor_role: 'platform_admin',
      action_type: 'feedback:created',
      resource_type: 'institution_feedback',
      resource_id: data.id,
      metadata: {
        organization_id: feedback.organization_id,
        category: feedback.category,
        feedback_type: feedback.feedback_type,
        assigned_team: assignedTeam,
      },
    });

    // Notify appropriate team (webhook/email integration point)
    await notifyTeam(data.id, feedback.category, feedback.organization_id);

    return data;
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.submission' },
      extra: { feedback },
    });
    throw error;
  }
}

/**
 * Update feedback status and trigger response workflow
 * Tracks all changes in feedback_updates audit table
 * 
 * @param feedbackId - Feedback ID to update
 * @param updates - Fields to update
 * @returns Updated feedback record
 */
export async function updateFeedbackStatus(
  feedbackId: string,
  updates: FeedbackUpdate
) {
  try {
    const supabase = await getSupabaseClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    const updateData: Record<string, unknown> = { ...updates };

    // If sending response, mark as sent
    if (updates.response_message) {
      updateData.response_sent = true;
      updateData.response_sent_at = new Date().toISOString();
    }

    // Update feedback
    const { data, error } = await supabase
      .from('institution_feedback')
      .update(updateData)
      .eq('id', feedbackId)
      .select()
      .single();

    if (error) throw error;

    // Log to audit trail
    await supabase.from('audit_logs').insert({
      actor_id: user.id,
      actor_role: 'platform_admin',
      action_type: 'feedback:updated',
      resource_type: 'institution_feedback',
      resource_id: feedbackId,
      metadata: {
        updates,
        organization_id: data.organization_id,
      },
    });

    return data;
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.update' },
      extra: { feedbackId, updates },
    });
    throw error;
  }
}

/**
 * Assign feedback to a team member
 * 
 * @param feedbackId - Feedback ID
 * @param assignedToUserId - User ID to assign to
 * @param team - Team name (optional)
 */
export async function assignFeedback(
  feedbackId: string,
  assignedToUserId: string,
  team?: string
) {
  const updates: FeedbackUpdate = {
    assigned_to: assignedToUserId,
    status: 'under_review',
  };

  if (team) {
    updates.assigned_team = team;
  }

  return updateFeedbackStatus(feedbackId, updates);
}

/**
 * Resolve feedback with response message
 * 
 * @param feedbackId - Feedback ID
 * @param responseMessage - Response to send back to institution
 */
export async function resolveFeedback(
  feedbackId: string,
  responseMessage: string
) {
  return updateFeedbackStatus(feedbackId, {
    status: 'resolved',
    response_message: responseMessage,
  });
}

/**
 * Mark feedback as won't fix with justification
 * 
 * @param feedbackId - Feedback ID
 * @param reason - Internal reason for not fixing
 */
export async function wontFixFeedback(
  feedbackId: string,
  reason: string
) {
  return updateFeedbackStatus(feedbackId, {
    status: 'wont_fix',
    internal_notes: reason,
  });
}

/**
 * Add tags to feedback for better categorization
 * 
 * @param feedbackId - Feedback ID
 * @param tags - Array of tag strings
 */
export async function addFeedbackTags(feedbackId: string, tags: string[]) {
  try {
    const supabase = await getSupabaseClient();
    const tagRecords = tags.map(tag => ({
      feedback_id: feedbackId,
      tag: tag.toLowerCase().trim(),
    }));

    const { error } = await supabase
      .from('feedback_tags')
      .upsert(tagRecords, { onConflict: 'feedback_id,tag' });

    if (error) throw error;

    return true;
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.tags' },
      extra: { feedbackId, tags },
    });
    throw error;
  }
}

/**
 * Get feedback statistics for an organization
 * 
 * @param organizationId - Organization ID
 * @returns Feedback stats
 */
export async function getFeedbackStats(organizationId?: string) {
  try {
    const supabase = await getSupabaseClient();
    let query = supabase.from('institution_feedback').select('id, status, created_at, resolved_at');

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const total = data?.length || 0;
    const resolved = data?.filter(f => f.status === 'resolved').length || 0;
    const inProgress = data?.filter(f => f.status === 'in_progress').length || 0;
    const newFeedback = data?.filter(f => f.status === 'new').length || 0;

    // Calculate average resolution time
    const resolvedWithTime = data?.filter(f => f.resolved_at && f.created_at) || [];
    const avgResolutionHours =
      resolvedWithTime.length > 0
        ? resolvedWithTime.reduce((sum, f) => {
            const created = new Date(f.created_at).getTime();
            const resolved = new Date(f.resolved_at!).getTime();
            const hours = (resolved - created) / (1000 * 60 * 60);
            return sum + hours;
          }, 0) / resolvedWithTime.length
        : 0;

    return {
      total,
      resolved,
      inProgress,
      newFeedback,
      resolutionRate: total > 0 ? (resolved / total) * 100 : 0,
      avgResolutionHours,
    };
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.stats' },
      extra: { organizationId },
    });
    throw error;
  }
}

/**
 * Get feedback by category for analytics
 * 
 * @param organizationId - Optional organization filter
 * @returns Category breakdown
 */
export async function getFeedbackByCategory(organizationId?: string) {
  try {
    const supabase = await getSupabaseClient();
    let query = supabase.from('institution_feedback').select('category, severity');

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const categoryStats = (data || []).reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = {
          category: item.category,
          count: 0,
          severityCounts: { critical: 0, high: 0, medium: 0, low: 0 },
        };
      }
      acc[item.category].count++;
      if (item.severity) {
        acc[item.category].severityCounts[item.severity]++;
      }
      return acc;
    }, {} as Record<string, unknown>);

    return Object.values(categoryStats);
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.analytics' },
    });
    throw error;
  }
}

/**
 * Internal function: Notify team of new feedback
 * This is a placeholder for webhook/email integration
 * 
 * @param feedbackId - Feedback ID
 * @param category - Feedback category
 * @param organizationId - Organization ID
 */
async function notifyTeam(
  feedbackId: string,
  category: string,
  organizationId: string
) {
  try {
    const team = TEAM_ROUTING[category] || 'Product Team';
    const supabase = await getSupabaseClient();

    // TODO: Implement actual notification mechanism
    // Options:
    // 1. Supabase Edge Function webhook
    // 2. Email via Resend/SendGrid
    // 3. Slack notification
    // 4. In-app notification

    console.log(`[Feedback Notification] ${team} - Feedback ${feedbackId} from org ${organizationId}`);

    // Log notification attempt
    await supabase.from('audit_logs').insert({
      action_type: 'feedback:notification_sent',
      resource_type: 'institution_feedback',
      resource_id: feedbackId,
      metadata: {
        team,
        organization_id: organizationId,
        category,
      },
    });
  } catch (error) {
    // Don't fail feedback submission if notification fails
    console.error('Failed to notify team:', error);
    captureException(error, {
      tags: { area: 'feedback.notification' },
    });
  }
}

/**
 * Generate feedback insights for a time period
 * 
 * @param organizationId - Organization ID (optional)
 * @param periodStart - Start date
 * @param periodEnd - End date
 * @returns Aggregated insights
 */
export async function generateFeedbackInsights(
  organizationId: string | null,
  periodStart: Date,
  periodEnd: Date
) {
  try {
    const supabase = await getSupabaseClient();
    let query = supabase
      .from('institution_feedback')
      .select('*')
      .gte('created_at', periodStart.toISOString())
      .lte('created_at', periodEnd.toISOString());

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Calculate insights
    const insights = {
      total_feedback: data?.length || 0,
      by_category: {} as Record<string, number>,
      by_severity: {} as Record<string, number>,
      by_status: {} as Record<string, number>,
      avg_resolution_time_hours: 0,
      top_issues: [] as Array<{ category: string; count: number }>,
    };

    (data || []).forEach(item => {
      insights.by_category[item.category] = (insights.by_category[item.category] || 0) + 1;
      insights.by_severity[item.severity] = (insights.by_severity[item.severity] || 0) + 1;
      insights.by_status[item.status] = (insights.by_status[item.status] || 0) + 1;
    });

    // Store insights in database
    const { error: insertError } = await supabase.from('feedback_insights').insert({
      organization_id: organizationId,
      insight_type: 'trend',
      title: `Feedback Insights: ${periodStart.toLocaleDateString()} - ${periodEnd.toLocaleDateString()}`,
      metrics: insights,
      data_snapshot: data,
      period_start: periodStart.toISOString(),
      period_end: periodEnd.toISOString(),
    });

    if (insertError) throw insertError;

    return insights;
  } catch (error) {
    captureException(error, {
      tags: { area: 'feedback.insights' },
    });
    throw error;
  }
}

export default {
  submitFeedback,
  updateFeedbackStatus,
  assignFeedback,
  resolveFeedback,
  wontFixFeedback,
  addFeedbackTags,
  getFeedbackStats,
  getFeedbackByCategory,
  generateFeedbackInsights,
};
